<template>
  <div class="pagination d-flex justify-content-center">
    <ul class="uk-pagination mt-3">
      <template v-if="pagination != null">
        <!-- Previous Button -->
        <li :class="[{ 'uk-disabled': isFirstPage }, 'pg-nav']">
          <a
            href="#"
            class="pg-nav-btn"
            :aria-disabled="isFirstPage ? 'true' : 'false'"
            :aria-label="$t('general.previous')"
            @click.prevent="goToPage(pagination.current_page - 1)"
          >
            <i class="uil-angle-left"></i>
          </a>
        </li>

        <!-- Page Numbers -->
        <template v-for="(page, index) in visiblePages">
          <li v-if="page === '...'" :key="'ellipsis-' + index" class="uk-disabled">
            <span>...</span>
          </li>
          <li v-else :key="'page-' + page" :class="{ 'uk-active': page === pagination.current_page }">
            <a
              href="#"
              class="pg-page-btn"
              :aria-current="page === pagination.current_page ? 'page' : null"
              :aria-disabled="page === pagination.current_page ? 'true' : 'false'"
              @click.prevent="goToPage(page)"
            >
              {{ page }}
            </a>
          </li>
        </template>

        <!-- Next Button -->
        <li :class="[{ 'uk-disabled': isLastPage }, 'pg-nav']">
          <a
            href="#"
            class="pg-nav-btn"
            :aria-disabled="isLastPage ? 'true' : 'false'"
            :aria-label="$t('general.next_pagination')"
            @click.prevent="goToPage(pagination.current_page + 1)"
          >
            <i class="uil-angle-right"></i>
          </a>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
export default {
  name: "Pagination",
  props: {
    pagination: {
      default: null,
      type: Object,
    },
    useIcons: {
      type: Boolean,
      default: false
    },
    maxVisible: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      viewportWidth: typeof window !== "undefined" ? window.innerWidth : 1200
    };
  },
  mounted() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  computed: {
    isFirstPage() {
      return !!this.pagination && this.pagination.current_page === 1;
    },
    isLastPage() {
      return !!this.pagination && this.pagination.current_page === this.pagination.last_page;
    },
    visiblePageCount() {
      if (this.viewportWidth <= 420) return 3;
      if (this.viewportWidth <= 768) return 5;
      if (this.viewportWidth <= 991) return 7;
      return Math.max(7, this.maxVisible);
    },
    visiblePages() {
      if (!this.pagination) return [];

      const current = this.pagination.current_page;
      const last = this.pagination.last_page;

      if (this.viewportWidth <= 576) {
        if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1);
        if (current <= 2) return [1, 2, "...", last];
        if (current >= last - 1) return [1, "...", last - 1, last];
        return [1, "...", current, "...", last];
      }

      if (this.viewportWidth <= 768) {
        if (last <= 6) return Array.from({ length: last }, (_, i) => i + 1);
        const pages = [1];
        const start = Math.max(2, current - 1);
        const end = Math.min(last - 1, current + 1);

        if (start > 2) pages.push("...");
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < last - 1) pages.push("...");

        pages.push(last);
        return pages;
      }

      const max = this.visiblePageCount;
      if (last <= max) {
        return Array.from({ length: last }, (_, i) => i + 1);
      }

      const pages = [];
      pages.push(1);
      const interiorSlots = Math.max(1, max - 2);
      let start = Math.max(2, current - Math.floor((interiorSlots - 1) / 2));
      let end = Math.min(last - 1, start + interiorSlots - 1);
      start = Math.max(2, end - interiorSlots + 1);

      if (start > 2) pages.push("...");

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < last - 1) pages.push("...");

      pages.push(last);
      return pages;
    }
  },
  methods: {
    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      if (page === this.pagination.current_page) return;
      this.$emit('changePage', page);
    },
    handleResize() {
      this.viewportWidth = window.innerWidth;
    }
  }
}
</script>
<style>
.uk-margin-small-top {
  margin-top: 0px !important;
}
</style>

<style scoped>
.pagination {
  width: 100%;
}

.pagination .uk-pagination {
  max-width: 100%;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  margin-right: 0 !important;
  padding-right: 0;
}

.pagination .uk-pagination > li {
  flex: 0 0 auto;
  padding-right: 0 !important;
  margin: 0 !important;
}

@media (max-width: 991px) {
  .pagination {
    margin-top: 12px;
  }

  .pagination .uk-pagination {
    margin: 0 !important;
    padding: 10px 12px;
    border: 1px solid #e8e9ec;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-wrap: nowrap;
    overflow: hidden;
  }

  .pagination .uk-pagination > li {
    margin: 0;
  }

  .pagination .uk-pagination > li > a,
  .pagination .uk-pagination > li > button,
  .pagination .uk-pagination > li > span {
    min-width: 36px;
    height: 40px;
    padding: 0 6px;
    border-radius: 10px;
    border: none;
    background: transparent;
    color: #5b667a;
    font-size: 18px;
    font-weight: 600;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
  }

  .pagination .uk-pagination > li > button:disabled {
    cursor: default;
  }

  .pagination .uk-pagination > li > a i,
  .pagination .uk-pagination > li > button i {
    font-size: 22px;
    font-weight: 600;
    color: #7b879d;
  }

  .pagination .uk-pagination > li.pg-nav > a.pg-nav-btn,
  .pagination .uk-pagination > li.pg-nav > button.pg-nav-btn {
    position: relative;
    overflow: hidden;
    min-width: 36px;
    height: 40px;
    border-radius: 999px;
    background: #f5f7fb;
    border: 1px solid #e1e6f0;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
    transform: translateZ(0);
    transition: transform 90ms ease, box-shadow 90ms ease, background-color 90ms ease, border-color 90ms ease;
  }

  .pagination .uk-pagination > li.pg-nav > a.pg-nav-btn i,
  .pagination .uk-pagination > li.pg-nav > button.pg-nav-btn i {
    font-size: 24px;
    color: #6f7d95;
  }

  .pagination .uk-pagination > li.pg-nav > a.pg-nav-btn::after,
  .pagination .uk-pagination > li.pg-nav > button.pg-nav-btn::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(111, 125, 149, 0.24);
    transform: translate(-50%, -50%) scale(1);
    opacity: 0;
    pointer-events: none;
  }

  .pagination .uk-pagination > li.pg-nav:not(.uk-disabled) > a.pg-nav-btn:active,
  .pagination .uk-pagination > li.pg-nav:not(.uk-disabled) > button.pg-nav-btn:active {
    transform: scale(0.94);
    background: #ebf0f8;
    border-color: #d3dceb;
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.16);
  }

  .pagination .uk-pagination > li.pg-nav:not(.uk-disabled) > a.pg-nav-btn:active::after,
  .pagination .uk-pagination > li.pg-nav:not(.uk-disabled) > button.pg-nav-btn:active::after {
    opacity: 1;
    transform: translate(-50%, -50%) scale(8);
    transition: transform 180ms ease, opacity 180ms ease;
  }

  .pagination .uk-pagination > li.uk-active > a,
  .pagination .uk-pagination > li.uk-active > button {
    min-width: 42px;
    height: 42px;
    border-radius: 10px;
    background: #2f5fd0;
    color: #fff;
    box-shadow: 0 4px 12px rgba(47, 95, 208, 0.24);
    transform: translateY(-1px);
  }

  .pagination .uk-pagination > li:not(.uk-active):not(.uk-disabled) > a:active,
  .pagination .uk-pagination > li:not(.uk-active):not(.uk-disabled) > button:active {
    background: #f2f5fb;
  }

  .pagination .uk-pagination > li.uk-disabled:not(.uk-active) > a,
  .pagination .uk-pagination > li.uk-disabled:not(.uk-active) > button,
  .pagination .uk-pagination > li.uk-disabled:not(.uk-active) > span {
    opacity: 0.45;
    pointer-events: none;
  }

  .pagination .uk-pagination > li.uk-disabled:not(.uk-active) > a i,
  .pagination .uk-pagination > li.uk-disabled:not(.uk-active) > button i {
    opacity: 0.8;
  }

  .pagination .uk-pagination > li.pg-nav.uk-disabled > a.pg-nav-btn,
  .pagination .uk-pagination > li.pg-nav.uk-disabled > button.pg-nav-btn {
    background: #f8f9fc;
    border-color: #e7ebf3;
    box-shadow: none;
  }
}

@media (max-width: 576px) {
  .pagination .uk-pagination {
    padding: 6px 7px;
    border-radius: 11px;
    gap: 3px;
  }

  .pagination .uk-pagination > li > a,
  .pagination .uk-pagination > li > button,
  .pagination .uk-pagination > li > span {
    min-width: 28px;
    height: 32px;
    font-size: 15px;
    border-radius: 8px;
    padding: 0 4px;
  }

  .pagination .uk-pagination > li > a i,
  .pagination .uk-pagination > li > button i {
    font-size: 18px;
  }

  .pagination .uk-pagination > li.pg-nav > a.pg-nav-btn,
  .pagination .uk-pagination > li.pg-nav > button.pg-nav-btn {
    min-width: 28px;
    height: 32px;
  }

  .pagination .uk-pagination > li.pg-nav > a.pg-nav-btn i,
  .pagination .uk-pagination > li.pg-nav > button.pg-nav-btn i {
    font-size: 18px;
  }

  .pagination .uk-pagination > li.uk-active > a,
  .pagination .uk-pagination > li.uk-active > button {
    min-width: 32px;
    height: 32px;
    border-radius: 8px;
    box-shadow: 0 3px 10px rgba(47, 95, 208, 0.18);
  }
}

@media (max-width: 420px) {
  .pagination {
    margin-top: 10px;
  }

  .pagination .uk-pagination {
    padding: 5px 6px;
    border-radius: 10px;
    gap: 2px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  }

  .pagination .uk-pagination > li > a,
  .pagination .uk-pagination > li > button,
  .pagination .uk-pagination > li > span {
    min-width: 26px;
    height: 30px;
    padding: 0 3px;
    font-size: 14px;
    font-weight: 600;
  }

  .pagination .uk-pagination > li > a i,
  .pagination .uk-pagination > li > button i,
  .pagination .uk-pagination > li.pg-nav > a.pg-nav-btn i,
  .pagination .uk-pagination > li.pg-nav > button.pg-nav-btn i {
    font-size: 16px;
  }

  .pagination .uk-pagination > li.pg-nav > a.pg-nav-btn,
  .pagination .uk-pagination > li.pg-nav > button.pg-nav-btn {
    min-width: 26px;
    height: 30px;
  }

  .pagination .uk-pagination > li.uk-active > a,
  .pagination .uk-pagination > li.uk-active > button {
    min-width: 30px;
    height: 30px;
    transform: none;
  }
}
</style>